import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { woocommerceApi } from "../../services/api";
import { useTranslation } from "../../contexts/TranslationContext";
import { ShoppingBagIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import DataTable, {
  Column,
  EmptyState,
  PaginationConfig,
} from "../common/DataTable";
import Badge from "../common/Badge";
import { WooCommerceOrder } from "../../types";

interface WooCommerceOrdersProps {
  itemsPerPage?: number;
}

const WooCommerceOrders: React.FC<WooCommerceOrdersProps> = ({
  itemsPerPage = 20,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);

  const { data: ordersData, isLoading } = useQuery(
    ["woocommerce-orders", currentPage],
    () =>
      woocommerceApi.getOrders(
        (currentPage - 1) * itemsPerPage,
        itemsPerPage,
        "received_at",
        "desc",
      ),
    {
      keepPreviousData: true,
    },
  );

  const syncMutation = useMutation(() => woocommerceApi.syncOrdersDeals(), {
    onSuccess: () => {
      queryClient.invalidateQueries(["woocommerce-orders"]);
    },
  });

  const handleRefresh = async () => {
    await syncMutation.mutateAsync();
  };

  const orders = ordersData?.items || [];
  const totalOrders = ordersData?.total || 0;
  const totalPages = Math.ceil(totalOrders / itemsPerPage);

  const getStatusVariant = (
    status: string,
  ): "success" | "warning" | "error" => {
    switch (status) {
      case "completed":
        return "success";
      case "processing":
      case "pending":
        return "warning";
      default:
        return "error";
    }
  };

  const columns: Column<WooCommerceOrder>[] = useMemo(
    () => [
      {
        key: "orderNumber",
        header: t("woocommerce.orders.orderNumber"),
        render: (order) => (
          <span className="text-sm font-medium text-gray-900">
            #{order.order_number}
          </span>
        ),
      },
      //   {
      //     key: "customer",
      //     header: t("woocommerce.orders.customer"),
      //     render: (order) => (
      //       <div>
      //         <p className="text-sm font-medium text-gray-900">
      //           {order.customer_first_name} {order.customer_last_name}
      //         </p>
      //         <p className="text-sm text-gray-500">{order.customer_email}</p>
      //       </div>
      //     ),
      //   },
      {
        key: "total",
        header: t("woocommerce.orders.total"),
        render: (order) => (
          <span className="text-sm text-gray-900">
            {order.total_amount.toFixed(2)} {order.currency.toUpperCase()}
          </span>
        ),
      },
      {
        key: "status",
        header: t("woocommerce.orders.status"),
        render: (order) => (
          <Badge
            label={order.status}
            variant={getStatusVariant(order.status)}
          />
        ),
      },
      {
        key: "dateCompleted",
        header: t("woocommerce.orders.dateCompleted"),
        render: (order) => (
          <span className="text-sm text-gray-500">
            {order.date_completed
              ? new Date(order.date_completed).toLocaleDateString()
              : "-"}
          </span>
        ),
      },
      {
        key: "pipedrive",
        header: t("woocommerce.orders.pipedriveLink"),
        render: (order) => {
          return (
            <span className="text-sm text-gray-900">
              {order.pipedrive_deal_id ? (
                <a
                  href={order.pipedrive_deal_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t("woocommerce.orders.pipedriveLink")}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 100 100"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0"
                      y="0"
                      width="100"
                      height="100"
                      rx="25"
                      ry="25"
                      fill="#26292c"
                    />

                    <text
                      x="50"
                      y="65"
                      fontFamily="sans-serif"
                      fontSize="75"
                      fontWeight="bold"
                      textAnchor="middle"
                      fill="#ffffff"
                    >
                      p
                    </text>
                  </svg>
                </a>
              ) : (
                "-"
              )}
            </span>
          );
        },
      },
    ],
    [t],
  );

  const emptyState: EmptyState = {
    icon: ShoppingBagIcon,
    title: t("woocommerce.orders.noOrders"),
    description: t("woocommerce.orders.noOrdersDescription"),
  };

  const pagination: PaginationConfig | undefined =
    totalPages > 1
      ? {
          currentPage,
          totalPages,
          totalItems: totalOrders,
          itemsPerPage,
          onPageChange: setCurrentPage,
        }
      : undefined;

  const refreshButton = (
    <button
      onClick={handleRefresh}
      disabled={syncMutation.isLoading}
      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      aria-label={t("woocommerce.orders.syncTooltip")}
    >
      <ArrowPathIcon
        className={`h-5 w-5 mr-2 ${
          syncMutation.isLoading ? "animate-spin" : ""
        }`}
      />
      <span>
        {t(
          syncMutation.isLoading
            ? "woocommerce.orders.syncing"
            : "woocommerce.orders.sync",
        )}
      </span>
    </button>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <DataTable
        data={orders}
        columns={columns}
        keyExtractor={(order) => order.id}
        title={t("woocommerce.orders.title")}
        emptyState={emptyState}
        pagination={pagination}
        loading={isLoading}
        showLoadingInHeader={true}
        headerActions={refreshButton}
      />
    </div>
  );
};

export default WooCommerceOrders;
